import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DISCLAIMER_TEXT } from "@/lib/constants";
import { ShieldCheck, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useLang } from "@/context/LangContext";
import BrandMark from "@/components/BrandMark";

export default function Register() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("ref") || "");
  const [check, setCheck] = useState({ status: "idle", referrer: null });
  const [accept, setAccept] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    if (!code || code.length < 4) {
      setCheck({ status: "idle", referrer: null });
      return;
    }
    setCheck({ status: "loading", referrer: null });
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/referral/validate/${code}`);
        if (data.valid) setCheck({ status: "valid", referrer: data.referrer_name });
        else setCheck({ status: "invalid", referrer: null });
      } catch {
        setCheck({ status: "invalid", referrer: null });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [code]);

  const proceed = () => {
    sessionStorage.setItem("sb_referral", code);
    sessionStorage.setItem("sb_agreed_terms", "true");
    sessionStorage.setItem("sb_agreed_terms_at", new Date().toISOString());
    navigate(`/login?ref=${encodeURIComponent(code)}`);
  };

  return (
    <div className="mx-auto max-w-xl px-4 py-12">
      <div className="text-center mb-8 flex flex-col items-center">
        <BrandMark size={140} />
        <div className="font-heading text-2xl mt-3">{t("register.title")}</div>
        <p className="text-sm text-muted-foreground mt-2 max-w-md">
          {t("register.subtitle")}
        </p>
      </div>

      <Card className="p-8 border-border/60 space-y-5">
        <div>
          <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
            {t("register.referral_label")}
          </label>
          <Input
            data-testid="register-referral-input"
            placeholder="e.g. SBABC123"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().trim())}
            className="mt-2 h-12 text-lg tracking-widest"
          />
          <div className="mt-2 text-sm min-h-[20px]">
            {check.status === "loading" && (
              <span className="flex items-center gap-1 text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("register.checking")}
              </span>
            )}
            {check.status === "valid" && (
              <span className="flex items-center gap-1.5 text-emerald-600" data-testid="referral-valid">
                <CheckCircle2 className="h-4 w-4" /> {t("register.referred_by")} <strong>{check.referrer}</strong>
              </span>
            )}
            {check.status === "invalid" && (
              <span className="flex items-center gap-1.5 text-destructive" data-testid="referral-invalid">
                <XCircle className="h-4 w-4" /> {t("register.invalid")}
              </span>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-primary/40 bg-primary/5 p-4">
          <div className="text-xs uppercase tracking-[0.16em] text-primary font-semibold mb-2">
            {t("register.policy_title")}
          </div>
          <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="register-accept-terms"
              checked={accept}
              onChange={(e) => setAccept(e.target.checked)}
              className="mt-1 h-4 w-4 accent-primary shrink-0"
            />
            <span className="leading-relaxed text-foreground">
              {t("register.accept")}
            </span>
          </label>
        </div>

        <div className="text-sm sm:text-base text-muted-foreground leading-relaxed p-4 rounded-xl bg-muted/60 border border-border/60">
          <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">{t("footer.disclaimer")}</div>
          {DISCLAIMER_TEXT}
        </div>

        <Button
          onClick={proceed}
          disabled={check.status !== "valid" || !accept}
          className="w-full h-12 rounded-full"
          data-testid="register-continue-btn"
        >
          {t("register.cta")}
        </Button>

        <div className="text-xs text-muted-foreground flex items-start gap-2">
          <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
          {t("register.review_notice")}
        </div>
      </Card>
    </div>
  );
}
